/**
 * ApplicationProfileCard — canvas renderer for the apply_application_profile
 * tool output (`artifact_type: 'application_profile'`).
 *
 * Shows the loaded profile so the user can verify Ion interpreted their
 * request correctly:
 *
 *   ┌────────────────────────────────────────────────────────┐
 *   │ 🔧 Power Tool                            [Reset to     │
 *   │                                            defaults]   │
 *   ├────────────────────────────────────────────────────────┤
 *   │ HARD CONSTRAINTS                                       │
 *   │ [P_cont_grav ≥ 800 W/kg]  [DCIR ≤ 15 mΩ]  [18650/21700]│
 *   ├────────────────────────────────────────────────────────┤
 *   │ SCORING WEIGHTS                    [AI] [flow default] │
 *   │ p_cont_grav  ──●───────  40%  ✓ yours                  │
 *   │ mass_g       ────●─────  30%                           │
 *   │ p_grav_peak  ──●───────  20%                           │
 *   │ cycle_life   ─●────────  10%                           │
 *   ├────────────────────────────────────────────────────────┤
 *   │ ⚠ Ion tracks no procurement availability — verify    │
 *   │   with supplier.                                       │
 *   └────────────────────────────────────────────────────────┘
 *
 * Slice A5: the weight bars are now `<input type=range>` sliders. Dragging a
 * slider debounces into a deterministic, non-LLM re-rank call
 * (reRankService.reRank) that updates the pinned ranked_cells_table + the
 * shared selection context (chatStore.applyReRankResult) — no chat quota
 * spent. See ion/sales-selection-contract.md §A5 for the frozen endpoint
 * contract this was built against (the backend half ships in parallel).
 *
 * Backend output shape (see F1-PLAN §5.2.2, extended by Flow-Schema v2):
 *   {
 *     artifact_type: 'application_profile',
 *     branch_label: string,                 // e.g. 'Power Tool'
 *     branch_slug?: string,                 // 'power_tool' | 'ebike' | ...
 *     intro?: string | null,                // v2: customer-visible markdown, shown at top
 *     hard_constraints: Record<string, ConstraintSpec>,
 *     scoring_weights: Record<string, number>,
 *     flow_default_weights?: Record<string, number>, // the flow's own weights, pre-AI/pre-user
 *     constraint_provenance?: Record<string, 'suggested'|'user_confirmed'>, // v2
 *     weighting_questions?: Array<{ slug, question, options?, maps_to, explanation }>, // v2
 *     weight_provenance?: Record<string, 'flow'|'ai_suggested'|'user_set'>, // A5 (forward-compat; may also live only in selection_context)
 *     must_ask_questions?: any[],            // rendered as ask_user later
 *     defaults?: Record<string, any>,
 *     disclaimers?: string[],
 *   }
 *
 *   ConstraintSpec ≈ { op: '>=' | '<=' | '==', value: number|string, unit?: string }
 */
import { useCallback, useEffect, useRef, useState } from 'preact/hooks'

import { t } from '../../../i18n/index.js'
import { labelForMetric } from './metricLabels.js'
import { BRANCH_ICONS } from '../../shared/Icons.jsx'
import {
  currentConversationId,
  applyReRankResult,
  selectionContext,
  reRankPending,
} from '../../../state/chatStore.js'
import { reRank } from '../../../services/reRankService.js'

// Math operators → readable technical symbols (≥/≤ instead of >=/<=) so a
// constraint reads like a clean datasheet line, not a code expression.
const OP_SYMBOL = { '>=': '≥', '<=': '≤', '==': '=', '>': '>', '<': '<' }

// Debounce window between the last slider drag tick and firing the re-rank
// call — short enough to feel live, long enough to coalesce a drag gesture
// into a single request (the backend also rate-limits, but there's no reason
// to spam it from a mousemove).
const RE_RANK_DEBOUNCE_MS = 300

/**
 * Format a single constraint into a readable technical string — units attached,
 * proper ≥/≤ symbols, allow-lists as "a or b". Accepts { op, value, unit },
 * { min, max, unit }, an allow-list array, or a primitive. Falls back to JSON
 * for unknown shapes so a malformed payload never crashes the UI.
 *
 *   { op: '>=', value: 8, unit: 'A' }   → "Continuous current ≥ 8 A"
 *   ['18650', '21700']                  → "Format: 18650 or 21700"
 *   { min: 3.3, max: 3.7, unit: 'V' }   → "3.3 ≤ Voltage ≤ 3.7 V"
 *   'NMC'                               → "Chemistry: NMC"
 */
function formatConstraint(label, spec) {
  if (spec == null) return `${label}: —`

  // Allow-list → "label: a or b"
  if (Array.isArray(spec)) {
    return `${label}: ${spec.join(' or ')}`
  }

  // Primitive (string / number / boolean) → "label: value"
  if (typeof spec !== 'object') {
    return `${label}: ${spec}`
  }

  // Structured { op, value, unit? }
  if ('op' in spec && 'value' in spec) {
    const unit = spec.unit ? ` ${spec.unit}` : ''
    const sym = OP_SYMBOL[spec.op] || spec.op
    return `${label} ${sym} ${spec.value}${unit}`
  }

  // Bound ranges { min, max, unit? }
  if ('min' in spec || 'max' in spec) {
    const unit = spec.unit ? ` ${spec.unit}` : ''
    if (spec.min != null && spec.max != null) {
      return `${spec.min} ≤ ${label} ≤ ${spec.max}${unit}`
    }
    if (spec.min != null) return `${label} ≥ ${spec.min}${unit}`
    if (spec.max != null) return `${label} ≤ ${spec.max}${unit}`
  }

  // Unknown shape — surface it without crashing.
  try {
    return `${label}: ${JSON.stringify(spec)}`
  } catch {
    return `${label}: [unrenderable]`
  }
}

/**
 * Normalise scoring weights to percentages and sort high→low.
 * Backend sends raw 0..1 floats that should sum to ~1.0 but we don't trust
 * that — re-normalise so the visual bars always read sensibly.
 *
 * NOTE: this is used ONLY for the read-only legacy display path (the % text
 * on a static bar). The editable slider list uses `rawWeightEntries` below,
 * which — unlike this function — keeps zero-weight keys, since a slider at 0
 * is a valid, meaningful position (the metric is deliberately turned off),
 * not something to drop from view.
 */
function normaliseWeights(weights) {
  if (!weights || typeof weights !== 'object') return []
  const entries = Object.entries(weights).filter(
    ([, v]) => Number.isFinite(v) && v > 0,
  )
  const sum = entries.reduce((acc, [, v]) => acc + v, 0)
  if (sum === 0) return []
  return entries
    .map(([key, v]) => ({ key, weight: v, pct: Math.round((v / sum) * 100) }))
    .sort((a, b) => b.weight - a.weight)
}

/**
 * ALL finite scoring-weight entries, including zero — sorted high→low. Unlike
 * `normaliseWeights`, nothing is dropped or re-normalised: sliders must show
 * (and let the user restore) a metric that's currently weighted at 0.
 */
function rawWeightEntries(weights) {
  if (!weights || typeof weights !== 'object') return []
  return Object.entries(weights)
    .filter(([, v]) => Number.isFinite(v))
    .sort((a, b) => b[1] - a[1])
    .map(([key, weight]) => ({ key, weight }))
}

/** Render the customer-visible `intro` (v2) as plain-text paragraphs (split on
 *  blank lines) — no markdown renderer pulled in for a short summary blurb. */
function IntroText({ intro }) {
  if (!intro) return null
  const paragraphs = String(intro)
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean)
  if (!paragraphs.length) return null
  return (
    <div class="ion-app-profile__intro">
      {paragraphs.map((p, i) => (
        <p key={i} class="ion-app-profile__intro-para">
          {p}
        </p>
      ))}
    </div>
  )
}

/** A single hard-constraint chip, with a (v2) provenance badge:
 *  'suggested' → a small "suggested" suffix chip (the AI proposed it, unconfirmed);
 *  'user_confirmed' → a "✓" prefix (the user explicitly confirmed it);
 *  absent → renders exactly as before (no badge). */
function ConstraintChip({ label, spec, provenance }) {
  return (
    <span class="ion-app-profile__chip">
      {provenance === 'user_confirmed' && (
        <span class="ion-app-profile__chip-confirmed" title="confirmed by you" aria-hidden="true">
          ✓{' '}
        </span>
      )}
      {formatConstraint(label, spec)}
      {provenance === 'suggested' && <span class="ion-app-profile__chip-badge">suggested</span>}
    </span>
  )
}

// Known option universes for allow-list constraints, so the toggle-chip editor
// can offer members the user hasn't picked yet (not just remove existing ones).
// Falls back to "current members only, remove-only" for any key not listed.
const ALLOW_LIST_OPTIONS = {
  mechanical_format: ['18650', '21700', '26650', '32700', '4680'],
}

/** Classify a ConstraintSpec so the editor knows which control to render. */
function specKind(spec) {
  if (Array.isArray(spec)) return 'allow_list'
  if (spec && typeof spec === 'object') {
    if ('not_in' in spec && Array.isArray(spec.not_in)) return 'not_in'
    if ('op' in spec && 'value' in spec) return 'op_value'
    if ('min' in spec || 'max' in spec) return 'range'
  }
  if (spec != null && typeof spec !== 'object') return 'primitive'
  return 'unknown'
}

/**
 * One interactive hard-constraint row (rail-friendly, stacked). Renders the
 * right editor for the spec shape and surfaces Confirm/Remove affordances.
 * All mutations flow up through the callbacks — this component holds no state
 * of its own; ConstraintsSection owns the authoritative constraint map.
 *
 *   ┌───────────────────────────────────────────────┐
 *   │ Continuous Current            [suggested]      │
 *   │ ≥ [   8 ] A                                    │
 *   │                          [✓ Confirm]  [✕]      │
 *   └───────────────────────────────────────────────┘
 */
function ConstraintControl({
  metricKey,
  label,
  spec,
  provenance,
  disabled,
  onConfirm,
  onEditSpec,
  onRemove,
}) {
  const kind = specKind(spec)

  const numberInput = (value, onNum, ariaLabel) => (
    <input
      type="number"
      class="ion-app-profile__constraint-input"
      value={value}
      disabled={disabled}
      aria-label={ariaLabel}
      onInput={(e) => {
        const n = Number(e.currentTarget.value)
        if (Number.isFinite(n)) onNum(n)
      }}
    />
  )

  let editor = null
  if (kind === 'op_value') {
    editor = (
      <div class="ion-app-profile__constraint-numeric">
        <span class="ion-app-profile__constraint-op">{OP_SYMBOL[spec.op] || spec.op}</span>
        {numberInput(spec.value, (n) => onEditSpec({ ...spec, value: n }), `${label} threshold`)}
        {spec.unit && <span class="ion-app-profile__constraint-unit">{spec.unit}</span>}
      </div>
    )
  } else if (kind === 'range') {
    editor = (
      <div class="ion-app-profile__constraint-numeric">
        {numberInput(spec.min ?? '', (n) => onEditSpec({ ...spec, min: n }), `${label} minimum`)}
        <span class="ion-app-profile__constraint-op">≤ {label} ≤</span>
        {numberInput(spec.max ?? '', (n) => onEditSpec({ ...spec, max: n }), `${label} maximum`)}
        {spec.unit && <span class="ion-app-profile__constraint-unit">{spec.unit}</span>}
      </div>
    )
  } else if (kind === 'allow_list') {
    const current = spec
    const options = Array.from(new Set([...(ALLOW_LIST_OPTIONS[metricKey] || []), ...current]))
    const toggle = (opt) => {
      const next = current.includes(opt)
        ? current.filter((v) => v !== opt)
        : [...current, opt]
      if (next.length === 0) onRemove()
      else onEditSpec(next)
    }
    editor = (
      <div class="ion-app-profile__constraint-chips">
        {options.map((opt) => {
          const active = current.includes(opt)
          return (
            <button
              key={opt}
              type="button"
              class={`ion-app-profile__toggle-chip${active ? ' ion-app-profile__toggle-chip--on' : ''}`}
              disabled={disabled}
              aria-pressed={active}
              onClick={() => toggle(opt)}
            >
              {opt}
            </button>
          )
        })}
      </div>
    )
  } else if (kind === 'not_in') {
    const values = spec.not_in
    const removeValue = (v) => {
      const next = values.filter((x) => x !== v)
      if (next.length === 0) onRemove()
      else onEditSpec({ ...spec, not_in: next })
    }
    const addValue = (raw) => {
      const v = String(raw).trim()
      if (v && !values.includes(v)) onEditSpec({ ...spec, not_in: [...values, v] })
    }
    editor = (
      <div class="ion-app-profile__constraint-chips">
        <span class="ion-app-profile__constraint-prefix">exclude</span>
        {values.map((v) => (
          <span key={v} class="ion-app-profile__value-chip">
            {v}
            <button
              type="button"
              class="ion-app-profile__value-chip-x"
              disabled={disabled}
              aria-label={`Stop excluding ${v}`}
              onClick={() => removeValue(v)}
            >
              ×
            </button>
          </span>
        ))}
        <input
          type="text"
          class="ion-app-profile__constraint-input ion-app-profile__constraint-input--text"
          placeholder="add…"
          disabled={disabled}
          aria-label={`Add a value to exclude from ${label}`}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              addValue(e.currentTarget.value)
              e.currentTarget.value = ''
            }
          }}
        />
      </div>
    )
  } else if (kind === 'primitive') {
    editor = (
      <input
        type="text"
        class="ion-app-profile__constraint-input ion-app-profile__constraint-input--text"
        value={String(spec)}
        disabled={disabled}
        aria-label={`${label} value`}
        onInput={(e) => onEditSpec(e.currentTarget.value)}
      />
    )
  } else {
    // Unknown shape — show it read-only rather than an editor we can't drive.
    editor = <span class="ion-app-profile__constraint-raw">{formatConstraint('', spec)}</span>
  }

  return (
    <li class="ion-app-profile__constraint">
      <div class="ion-app-profile__constraint-head">
        <span class="ion-app-profile__constraint-label">{label}</span>
        {provenance === 'user_confirmed' && (
          <span class="ion-app-profile__chip-confirmed" title="confirmed by you">
            ✓ yours
          </span>
        )}
        {provenance === 'suggested' && (
          <span class="ion-app-profile__chip-badge">suggested</span>
        )}
        <button
          type="button"
          class="ion-app-profile__constraint-remove"
          disabled={disabled}
          aria-label={`Remove ${label} constraint`}
          title="Remove this requirement"
          onClick={onRemove}
        >
          ×
        </button>
      </div>
      {editor}
      {provenance === 'suggested' && (
        <div class="ion-app-profile__constraint-actions">
          <button
            type="button"
            class="ion-app-profile__btn ion-app-profile__btn--ghost ion-app-profile__btn--tiny"
            disabled={disabled}
            onClick={onConfirm}
          >
            ✓ Confirm
          </button>
        </div>
      )}
    </li>
  )
}

/**
 * Editable Hard-Constraints section. Mirrors WeightSlidersSection's
 * re-rank mechanics (300ms debounce, in-flight `reRankPending`, unmount flush,
 * readable error). Owns the authoritative constraint map locally (seeded once
 * from props) — the re-rank endpoint returns a ranked_cells_table, not a fresh
 * application_profile, so nothing re-seeds these from props after interaction;
 * successful re-ranks sync from the returned `selection_context` instead.
 *
 * Interactivity is gated the same way the weight sliders are: active only once
 * a candidate set exists (`reRankReady`, i.e. candidateSet ≥ 2), because the
 * endpoint 409s on a smaller set. Before that the suggested constraints show
 * read-only with the same "once we've shortlisted candidates" hint.
 */
function ConstraintsSection({
  hardConstraints,
  constraintProvenance,
  liveConstraintProvenance,
  publicId,
  reRankReady,
}) {
  const [localConstraints, setLocalConstraints] = useState(() => ({ ...(hardConstraints || {}) }))
  const [provenance, setProvenance] = useState(() => ({ ...(constraintProvenance || {}) }))
  const [status, setStatus] = useState('idle') // 'idle' | 'pending' | 'error'
  const [errorMessage, setErrorMessage] = useState(null)
  const debounceRef = useRef(null)
  const pendingRef = useRef(null) // { override } awaiting a debounced re-rank
  const publicIdRef = useRef(publicId)
  publicIdRef.current = publicId

  // Overlay the LIVE selection_context provenance on the artifact baseline when
  // it changes upstream (e.g. Ion confirmed a suggested constraint by answering
  // a reduction question — the live context updates without any action in this
  // section). Without this, useState's once-only initializer leaves the badge
  // stuck on "suggested" until an unrelated re-rank happens to refresh it.
  // Guarded on pendingRef so a threshold the user is mid-way typing (debounced,
  // not yet fired) is never clobbered by an incoming update.
  useEffect(() => {
    if (pendingRef.current) return
    setProvenance({ ...(constraintProvenance || {}), ...(liveConstraintProvenance || {}) })
  }, [constraintProvenance, liveConstraintProvenance])

  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    // Flush a pending edit on unmount (e.g. a tab switch inside the debounce
    // window) so a typed threshold is never silently lost — same pattern as
    // WeightSlidersSection.
    const pending = pendingRef.current
    if (pending && publicIdRef.current) {
      pendingRef.current = null
      reRankPending.value = true
      reRank(publicIdRef.current, { hardConstraints: pending })
        .then((result) => {
          reRankPending.value = false
          applyReRankResult(result)
        })
        .catch(() => {
          reRankPending.value = false
        })
    }
  }, [])

  const syncFromResult = useCallback((result) => {
    const sc = result?.selection_context
    if (!sc) return
    if (sc.hard_constraints) setLocalConstraints({ ...sc.hard_constraints })
    if (sc.constraint_provenance) setProvenance({ ...sc.constraint_provenance })
  }, [])

  const fireReRank = useCallback((override) => {
    if (!publicId) return
    pendingRef.current = null
    setStatus('pending')
    reRankPending.value = true
    setErrorMessage(null)
    reRank(publicId, { hardConstraints: override })
      .then((result) => {
        setStatus('idle')
        reRankPending.value = false
        applyReRankResult(result)
        syncFromResult(result)
      })
      .catch((err) => {
        setStatus('error')
        reRankPending.value = false
        setErrorMessage(err?.message || 'Re-rank failed')
      })
  }, [publicId, syncFromResult])

  const scheduleReRank = useCallback((override) => {
    pendingRef.current = override
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => fireReRank(override), RE_RANK_DEBOUNCE_MS)
  }, [fireReRank])

  // Confirming a suggested constraint re-ranks with the current spec unchanged;
  // the backend flips its provenance to user_confirmed.
  const handleConfirm = useCallback((key) => {
    setProvenance((prev) => ({ ...prev, [key]: 'user_confirmed' }))
    fireReRank({ [key]: localConstraints[key] })
  }, [fireReRank, localConstraints])

  // Editing a threshold implies confirming it. Optimistically update local
  // state so the field is authoritative even if the response omits the echo,
  // then debounce the re-rank.
  const handleEditSpec = useCallback((key, nextSpec) => {
    setLocalConstraints((prev) => ({ ...prev, [key]: nextSpec }))
    setProvenance((prev) => ({ ...prev, [key]: 'user_confirmed' }))
    scheduleReRank({ [key]: nextSpec })
  }, [scheduleReRank])

  const handleRemove = useCallback((key) => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    setLocalConstraints((prev) => {
      const next = { ...prev }
      delete next[key]
      return next
    })
    setProvenance((prev) => {
      const next = { ...prev }
      delete next[key]
      return next
    })
    fireReRank({ [key]: null })
  }, [fireReRank])

  const entries = Object.entries(localConstraints)
  if (entries.length === 0) return null

  const disabled = !publicId || !reRankReady

  // Read-only gate — before a candidate set exists there's nothing to re-rank
  // (the endpoint 409s), so show the suggested constraints as static chips with
  // the same explanatory hint the weight sliders use.
  if (disabled) {
    return (
      <section class="ion-app-profile__section">
        <h4 class="ion-app-profile__section-title">Hard Constraints</h4>
        <div class="ion-app-profile__chips">
          {entries.map(([key, spec]) => (
            <ConstraintChip
              key={key}
              label={labelForMetric(key)}
              spec={spec}
              provenance={provenance[key] || constraintProvenance?.[key]}
            />
          ))}
        </div>
        <p class="ion-app-profile__weight-status">
          These are Ion's suggested requirements — you'll be able to confirm or
          adjust them once we've shortlisted candidates.
        </p>
      </section>
    )
  }

  return (
    <section class="ion-app-profile__section">
      <h4 class="ion-app-profile__section-title">Hard Constraints</h4>
      <ul class="ion-app-profile__constraints">
        {entries.map(([key, spec]) => (
          <ConstraintControl
            key={key}
            metricKey={key}
            label={labelForMetric(key)}
            spec={spec}
            provenance={provenance[key] || constraintProvenance?.[key]}
            disabled={false}
            onConfirm={() => handleConfirm(key)}
            onEditSpec={(nextSpec) => handleEditSpec(key, nextSpec)}
            onRemove={() => handleRemove(key)}
          />
        ))}
      </ul>
      {status === 'error' && (
        <p class="ion-app-profile__weight-status ion-app-profile__weight-status--error" role="alert">
          Re-rank failed{errorMessage ? `: ${errorMessage}` : ''}.
        </p>
      )}
    </section>
  )
}

/** Weight-provenance badge, shown after the % read-out on a slider row:
 *  'user_set' → "✓ yours" (the user dragged this slider);
 *  'ai_suggested' → "AI suggested" (the AI adjusted it away from the flow default);
 *  'flow' / absent → no badge (unmodified flow default — the common case). */
function WeightProvenanceBadge({ provenance }) {
  if (provenance === 'user_set') {
    return (
      <span class="ion-app-profile__weight-badge ion-app-profile__weight-badge--user-set">
        ✓ yours
      </span>
    )
  }
  if (provenance === 'ai_suggested') {
    return <span class="ion-app-profile__weight-badge">AI suggested</span>
  }
  return null
}

/**
 * Editable scoring-weight sliders + reset affordances + "why these weights?"
 * explanation. Owns its own local state (seeded once from `weights`) so
 * dragging feels instant; each change debounces into a re-rank call.
 *
 * `weights`/`weightProvenance` are the INITIAL values carried by this
 * artifact — once the user starts interacting, this component's local state
 * is authoritative for what it displays (the re-rank endpoint doesn't emit a
 * new application_profile artifact, so nothing re-seeds it from props).
 */
function WeightSlidersSection({
  weights,
  flowDefaultWeights,
  weightProvenance,
  weightingQuestions,
  publicId,
  reRankReady,
}) {
  const entries = rawWeightEntries(weights)
  const baselineRef = useRef(weights || {})
  const [localWeights, setLocalWeights] = useState(() => ({ ...(weights || {}) }))
  const [provenance, setProvenance] = useState(() => ({ ...(weightProvenance || {}) }))
  const [status, setStatus] = useState('idle') // 'idle' | 'pending' | 'error'
  const [errorMessage, setErrorMessage] = useState(null)
  const debounceRef = useRef(null)
  // Weights awaiting a debounced re-rank — kept in a ref so the unmount
  // cleanup can flush them (see below).
  const pendingRef = useRef(null)
  // Latest publicId, readable from the (deps: []) cleanup closure.
  const publicIdRef = useRef(publicId)
  publicIdRef.current = publicId

  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    // Flush a pending re-rank on unmount — e.g. the user drags a slider and
    // immediately switches the Plot/Table tab (which unmounts this card)
    // within the debounce window. Without this the drag is silently lost.
    // Fire-and-forget: applyReRankResult updates global chatStore signals, so
    // the ranking still refreshes even though this card is gone; no setState
    // here (the component is unmounting).
    const pending = pendingRef.current
    if (pending && publicIdRef.current) {
      pendingRef.current = null
      reRankPending.value = true
      reRank(publicIdRef.current, { scoringWeights: pending })
        .then((result) => {
          reRankPending.value = false
          applyReRankResult(result)
        })
        .catch(() => {
          reRankPending.value = false
        })
    }
  }, [])

  const fireReRank = useCallback((nextWeights) => {
    if (!publicId) return
    pendingRef.current = null
    setStatus('pending')
    // Surface the in-flight state to the canvas too, so the persistent
    // profile panel's plot/table below can show the same "Updating…" shimmer
    // a streaming chat turn shows (see chatStore.reRankPending).
    reRankPending.value = true
    setErrorMessage(null)
    reRank(publicId, { scoringWeights: nextWeights })
      .then((result) => {
        setStatus('idle')
        reRankPending.value = false
        applyReRankResult(result)
        const wp = result?.selection_context?.weight_provenance
        if (wp && typeof wp === 'object') {
          setProvenance((prev) => ({ ...prev, ...wp }))
        }
      })
      .catch((err) => {
        setStatus('error')
        reRankPending.value = false
        setErrorMessage(err?.message || 'Re-rank failed')
      })
  }, [publicId])

  const scheduleReRank = useCallback((nextWeights) => {
    pendingRef.current = nextWeights
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => fireReRank(nextWeights), RE_RANK_DEBOUNCE_MS)
  }, [fireReRank])

  const handleSliderChange = useCallback((key, value) => {
    setLocalWeights((prev) => {
      const next = { ...prev, [key]: value }
      scheduleReRank(next)
      return next
    })
    setProvenance((prev) => ({ ...prev, [key]: 'user_set' }))
  }, [scheduleReRank])

  const handleResetAiSuggestion = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    const baseline = { ...baselineRef.current }
    setLocalWeights(baseline)
    setProvenance((prev) => {
      const next = { ...prev }
      for (const key of Object.keys(baseline)) next[key] = weightProvenance?.[key] || 'flow'
      return next
    })
    fireReRank(baseline)
  }, [fireReRank, weightProvenance])

  const handleResetFlowDefault = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    // The flow's OWN weights — for the imported flows, the reference author's
    // numbers verbatim. The artifact carries them separately from
    // `scoring_weights`, which the AI may already have nudged; before that
    // field existed this button fell back to the same baseline as "Reset to AI
    // suggestion", so two buttons did one thing and the one labelled "flow
    // default" was the one that lied (2026-08-10).
    const flowDefault = { ...(flowDefaultWeights || baselineRef.current) }
    setLocalWeights(flowDefault)
    setProvenance((prev) => {
      const next = { ...prev }
      for (const key of Object.keys(flowDefault)) next[key] = 'flow'
      return next
    })
    fireReRank(flowDefault)
  }, [fireReRank, flowDefaultWeights])

  if (entries.length === 0) return null

  // Re-rank only makes sense once cells have actually been ranked into a
  // candidate set (the weighting phase). Before that — during the reduction /
  // requirement questions — the backend has no candidate_set to re-rank and
  // would 409, so the sliders stay read-only and show the AI's proposed
  // starting weights. `reRankReady` mirrors the backend precondition
  // (candidate_set >= 2).
  const disabled = !publicId || !reRankReady

  return (
    <section class="ion-app-profile__section">
      <div class="ion-app-profile__weights-header">
        <h4 class="ion-app-profile__section-title">Scoring Weights</h4>
        <div class="ion-app-profile__weight-resets">
          <button
            type="button"
            class="ion-app-profile__btn ion-app-profile__btn--ghost ion-app-profile__btn--tiny"
            onClick={handleResetAiSuggestion}
            disabled={disabled}
            title="Back to the weighting Ion proposed for this conversation, including anything it adjusted from your answers."
          >
            Reset to AI suggestion
          </button>
          <button
            type="button"
            class="ion-app-profile__btn ion-app-profile__btn--ghost ion-app-profile__btn--tiny"
            onClick={handleResetFlowDefault}
            disabled={disabled}
            title="Back to the reference weighting this profile was defined with — no conversation, no adjustment."
          >
            Reset to flow default
          </button>
        </div>
      </div>

      <ul class="ion-app-profile__weights ion-app-profile__weights--interactive">
        {(() => {
          // The displayed %-labels are the RELATIVE share of each weight (they
          // sum to 100), matching how the backend re-normalises the weights for
          // scoring — so three sliders never read "85 / 30 / 55%". The slider
          // thumb position stays at the RAW 0..1 value the user set.
          const weightSum = entries.reduce(
            (s, { key }) => s + (Number.isFinite(localWeights[key]) ? localWeights[key] : 0),
            0,
          )
          return entries.map(({ key }) => {
            const value = Number.isFinite(localWeights[key]) ? localWeights[key] : 0
            const fillPct = Math.round(value * 100)
            const displayPct = weightSum > 0 ? Math.round((value / weightSum) * 100) : 0
            return (
              <li key={key} class="ion-app-profile__weight-row ion-app-profile__weight-row--interactive">
                <span class="ion-app-profile__weight-label">{labelForMetric(key)}</span>
                <input
                  type="range"
                  class="ion-app-profile__weight-slider"
                  min="0"
                  max="1"
                  step="0.01"
                  value={value}
                  disabled={disabled}
                  aria-label={`${labelForMetric(key)} scoring weight`}
                  style={`--_pct: ${fillPct}`}
                  onInput={(e) => handleSliderChange(key, Number(e.currentTarget.value))}
                />
                <span class="ion-app-profile__weight-pct">{displayPct}%</span>
                <WeightProvenanceBadge provenance={provenance[key]} />
              </li>
            )
          })
        })()}
      </ul>

      {!reRankReady && (
        <p class="ion-app-profile__weight-status">
          These are Ion's proposed starting weights — you'll be able to fine-tune
          them once we've shortlisted candidates.
        </p>
      )}
      {status === 'pending' && (
        <p class="ion-app-profile__weight-status" role="status">Re-ranking…</p>
      )}
      {status === 'error' && (
        <p class="ion-app-profile__weight-status ion-app-profile__weight-status--error" role="alert">
          Re-rank failed{errorMessage ? `: ${errorMessage}` : ''}.
        </p>
      )}

      {Array.isArray(weightingQuestions) && weightingQuestions.length > 0 && (
        <details class="ion-app-profile__weight-why">
          <summary>Why these weights?</summary>
          <ul class="ion-app-profile__weight-why-list">
            {weightingQuestions.map((q, i) => (
              <li key={q?.slug || i} class="ion-app-profile__weight-why-item">
                {q?.question && <strong>{q.question}</strong>}
                {q?.explanation && <p>{q.explanation}</p>}
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  )
}

/** The profile body (constraints + weights + disclaimers) — shared by both
 *  the canvas card and the inline chat block. */
function ProfileSections({
  hardConstraints,
  disclaimers,
  constraintProvenance,
  liveConstraintProvenance,
  scoringWeights,
  flowDefaultWeights,
  weightProvenance,
  weightingQuestions,
  publicId,
  reRankReady,
}) {
  return (
    <>
      <ConstraintsSection
        hardConstraints={hardConstraints}
        constraintProvenance={constraintProvenance}
        liveConstraintProvenance={liveConstraintProvenance}
        publicId={publicId}
        reRankReady={reRankReady}
      />

      <WeightSlidersSection
        weights={scoringWeights}
        flowDefaultWeights={flowDefaultWeights}
        weightProvenance={weightProvenance}
        weightingQuestions={weightingQuestions}
        publicId={publicId}
        reRankReady={reRankReady}
      />

      {disclaimers.length > 0 && (
        <details class="ion-app-profile__caveats">
          <summary class="ion-app-profile__caveats-summary">
            <span class="ion-app-profile__caveats-icon" aria-hidden="true">ⓘ</span>
            What Ion can{'’'}t check ({disclaimers.length})
          </summary>
          <footer class="ion-app-profile__disclaimers" aria-label="Disclaimers">
            {disclaimers.map((line, idx) => (
              <p key={idx} class="ion-app-profile__disclaimer">
                <span aria-hidden="true">⚠ </span>
                {line}
              </p>
            ))}
          </footer>
        </details>
      )}
    </>
  )
}

/**
 * @param {object} props
 * @param {object} props.data — the apply_application_profile tool output payload
 * @param {'canvas'|'chat'} [props.variant='canvas'] — 'chat' renders a compact,
 *   collapsible block for the message flow (the constraints now live in the
 *   chat, not the canvas — the canvas shows the live Ragone instead).
 */
export function ApplicationProfileCard({ data, variant = 'canvas' }) {
  if (!data) return null

  const branchSlug = data.branch_slug || null
  const BranchIcon = branchSlug ? BRANCH_ICONS[branchSlug] : null
  const branchLabel =
    data.branch_label ||
    (branchSlug ? t(`ion.workflow.branchLabel.${branchSlug}`) : 'Application')

  const disclaimers = Array.isArray(data.disclaimers) ? data.disclaimers : []
  const constraintProvenance = data.constraint_provenance || null
  // The LIVE selection_context provenance, if any. Ion can confirm a suggested
  // constraint by answering a reduction question (e.g. picking a cell format)
  // AFTER this profile artifact was emitted — that updates the selection_context
  // but NOT this frozen `data` snapshot. ConstraintsSection overlays this on the
  // artifact baseline so the "suggested" vs "✓ yours" badges stay truthful
  // instead of lagging until the user happens to trigger a re-rank. (A stable
  // reference while the signal is unchanged — safe as an effect dependency.)
  const liveConstraintProvenance = selectionContext.value?.constraintProvenance || null
  const publicId = currentConversationId.value
  // The weight sliders are interactive only once the flow has a ranked
  // candidate set (>= 2 cells) — i.e. we've reached the weighting phase.
  // This mirrors the /re-rank endpoint's own precondition, so a slider is
  // never draggable when the backend would reject the re-rank with a 409.
  const candidateCount = (selectionContext.value?.candidateSet || []).length
  const reRankReady = candidateCount >= 2
  const sections = (
    <ProfileSections
      hardConstraints={data.hard_constraints}
      disclaimers={disclaimers}
      constraintProvenance={constraintProvenance}
      liveConstraintProvenance={liveConstraintProvenance}
      scoringWeights={data.scoring_weights}
      flowDefaultWeights={data.flow_default_weights}
      weightProvenance={data.weight_provenance}
      weightingQuestions={data.weighting_questions}
      publicId={publicId}
      reRankReady={reRankReady}
    />
  )

  // Chat variant — a calm, collapsible block inline in the conversation.
  if (variant === 'chat') {
    return (
      <details class="ion-app-profile ion-app-profile--chat" open>
        <summary class="ion-app-profile__summary">
          {BranchIcon && (
            <span class="ion-app-profile__icon" aria-hidden="true">
              <BranchIcon size={18} />
            </span>
          )}
          <span class="ion-app-profile__summary-label">Application profile · {branchLabel}</span>
        </summary>
        <IntroText intro={data.intro} />
        {sections}
      </details>
    )
  }

  // Canvas variant — the PERSISTENT control surface ArtifactCanvas mounts
  // above the Table/Plot/Report tabs for the whole selection flow (see
  // chatStore.latestApplicationProfile). Deliberately compact: no header
  // actions (the real resets live in WeightSlidersSection's own buttons —
  // the old disabled "Reset — Phase 2" stub is gone), and the intro collapses
  // into a <details> so the constraints/weights stay the focus and the
  // plot/table below keep most of the vertical space.
  return (
    <article class="ion-app-profile ion-app-profile--canvas" aria-label={`Application profile: ${branchLabel}`}>
      <header class="ion-app-profile__header ion-app-profile__header--canvas">
        <h3 class="ion-app-profile__title">
          {BranchIcon && (
            <span class="ion-app-profile__icon" aria-hidden="true">
              <BranchIcon size={20} />
            </span>
          )}
          <span>{branchLabel}</span>
        </h3>
      </header>
      {data.intro && (
        <details class="ion-app-profile__intro-toggle">
          <summary>About this profile</summary>
          <IntroText intro={data.intro} />
        </details>
      )}
      {sections}
    </article>
  )
}

// Exported for tests — pure helpers, no JSX coupling.
export const __test__ = { formatConstraint, normaliseWeights, rawWeightEntries }
